import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Sparkles } from 'lucide-react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { templatesAPI } from '../services/api';
import SetupErrorMessage from './SetupErrorMessage';
import './TemplateBuilder.css';

// Custom Table Blot - treat entire table as atomic block
const BlockEmbed = Quill.import('blots/block/embed');

// Helper to clean table HTML (remove Google Sheets junk)
const cleanTableHTML = (html) => {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  const table = temp.querySelector('table');

  if (!table) return html;

  // Remove unnecessary attributes
  const attributesToRemove = ['xmlns', 'data-sheets-root', 'data-sheets-baot', 'cellspacing', 'cellpadding', 'dir'];
  attributesToRemove.forEach(attr => table.removeAttribute(attr));

  // Clean all child elements (remove inline styles, keep structure)
  const cleanElement = (element) => {
    Array.from(element.attributes).forEach(attr => {
      if (attr.name.startsWith('data-sheets-') || attr.name === 'style' || attr.name === 'class') {
        element.removeAttribute(attr.name);
      }
    });
    Array.from(element.children).forEach(child => cleanElement(child));
  };

  // Remove colgroup
  const colgroup = table.querySelector('colgroup');
  if (colgroup) colgroup.remove();

  // Clean all child elements
  cleanElement(table);

  // Set simple border attribute
  table.setAttribute('border', '1');

  return table.outerHTML;
};

class TableBlot extends BlockEmbed {
  static blotName = 'table';
  static tagName = 'table';
  static className = 'ql-table';

  static create(value) {
    const node = super.create();
    if (typeof value === 'string') {
      const cleanedHTML = cleanTableHTML(value);
      const temp = document.createElement('div');
      temp.innerHTML = cleanedHTML;
      const table = temp.querySelector('table');

      if (table) {
        Array.from(table.attributes).forEach(attr => {
          node.setAttribute(attr.name, attr.value);
        });
        node.innerHTML = table.innerHTML;
      } else {
        node.innerHTML = cleanedHTML;
      }
    }
    return node;
  }

  static value(node) {
    return node.outerHTML;
  }
}

Quill.register(TableBlot, true);

const createDefaultMessage = () => ({
  id: Date.now(),
  type: 'email',
  delay_value: 0,
  delay_unit: 'hours',  // 'hours' or 'days'
  subject: '',
  body: '',
  provider: 'smtp'  // Default to SMTP
});

const AVAILABLE_PLACEHOLDERS = [
  { label: 'Business Name', value: '{business_name}' },
  { label: 'Industry', value: '{industry}' },
  { label: 'Location', value: '{location}' }
];

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['link'],
    ['clean']
  ],
  clipboard: {
    matchVisual: false,
    matchers: [
      // Custom matcher for tables - convert entire table to TableBlot
      ['TABLE', (node, delta) => {
        const Delta = Quill.import('delta');
        const tableHTML = node.outerHTML;
        return new Delta().insert({ table: tableHTML });
      }],
      // Prevent individual table elements from being processed separately
      ['TBODY', () => new (Quill.import('delta'))()],
      ['THEAD', () => new (Quill.import('delta'))()],
      ['TR', () => new (Quill.import('delta'))()],
      ['TD', () => new (Quill.import('delta'))()],
      ['TH', () => new (Quill.import('delta'))()]
    ]
  }
};

const TemplateBuilder = ({ onSave, initialData = null, onCancel }) => {
  const [templateName, setTemplateName] = useState('');
  const [description, setDescription] = useState('');
  const [industry, setIndustry] = useState('');
  const [location, setLocation] = useState('');
  const [campaignGoal, setCampaignGoal] = useState('');
  const [emailProvider, setEmailProvider] = useState('smtp');
  const [messages, setMessages] = useState([createDefaultMessage()]);
  const [validationError, setValidationError] = useState('');
  const [aiSubjectLoading, setAiSubjectLoading] = useState({});
  const [aiBodyLoading, setAiBodyLoading] = useState({});
  const [aiError, setAiError] = useState('');
  const [htmlEditorStates, setHtmlEditorStates] = useState({}); // Track HTML mode per message

  // Refs to store Quill editor instances and subject input refs
  const quillRefs = useRef({});
  const subjectInputRefs = useRef({});

  useEffect(() => {
    const metadata = initialData?.json_config?.metadata || {};
    setTemplateName(initialData?.name || '');
    setDescription(initialData?.description || '');
    setIndustry(initialData?.industry || metadata.industry || '');
    setLocation(initialData?.location || metadata.location || '');
    setCampaignGoal(initialData?.campaign_goal || metadata.campaign_goal || '');

    // Extract email provider from first email message or use smtp as default
    const firstEmailMsg = initialData?.json_config?.messages?.find(msg => msg.type === 'email');
    setEmailProvider(firstEmailMsg?.provider || metadata.emailProvider || 'smtp');

    if (initialData?.json_config?.messages?.length) {
      const mapped = initialData.json_config.messages.map((msg, idx) => {
        const delayHours = typeof msg.delay_hours === 'number' ? msg.delay_hours : 0;
        // Convert to days if divisible by 24 and >= 24, otherwise keep as hours
        const delayUnit = delayHours >= 24 && delayHours % 24 === 0 ? 'days' : 'hours';
        const delayValue = delayUnit === 'days' ? delayHours / 24 : delayHours;

        return {
          id: Date.now() + idx,
          type: msg.type || 'email',
          delay_value: delayValue,
          delay_unit: delayUnit,
          subject: msg.subject || '',
          body: msg.body || '',
          provider: msg.provider || 'smtp'  // Preserve or default to SMTP
        };
      });
      setMessages(mapped);
    } else {
      setMessages([createDefaultMessage()]);
    }

    setAiSubjectLoading({});
    setAiBodyLoading({});
    setAiError('');
  }, [initialData]);

  const addMessage = () => {
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: 'email',
        delay_minutes: prev.length === 0 ? 0 : 60,
        subject: '',
        body: ''
      }
    ]);
  };

  const updateMessage = (id, field, value) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.id === id ? { ...msg, [field]: value } : msg
      )
    );
  };

  const removeMessage = (id) => {
    if (messages.length === 1) {
      setValidationError('Template must have at least one message');
      setTimeout(() => setValidationError(''), 3000);
      return;
    }
    setMessages((prev) => prev.filter(msg => msg.id !== id));
  };

  const insertPlaceholder = (messageId, field, placeholder) => {
    const message = messages.find(m => m.id === messageId);
    if (!message) return;

    if (field === 'body' && message.type === 'email') {
      // For ReactQuill editor, insert at cursor position
      const quillRef = quillRefs.current[messageId];
      if (quillRef) {
        const quill = quillRef.getEditor();
        const selection = quill.getSelection();
        const cursorPosition = selection ? selection.index : quill.getLength();
        quill.insertText(cursorPosition, placeholder);
        quill.setSelection(cursorPosition + placeholder.length);
      } else {
        // Fallback if ref not available
        const currentValue = message[field] || '';
        const updatedValue = `${currentValue} ${placeholder}`.trim();
        updateMessage(messageId, field, updatedValue);
      }
    } else if (field === 'subject') {
      // For subject input field, insert at cursor position
      const inputRef = subjectInputRefs.current[messageId];
      if (inputRef) {
        const cursorPosition = inputRef.selectionStart || 0;
        const currentValue = message[field] || '';
        const before = currentValue.substring(0, cursorPosition);
        const after = currentValue.substring(cursorPosition);
        const updatedValue = `${before}${placeholder}${after}`;
        updateMessage(messageId, field, updatedValue);

        // Set cursor position after the inserted placeholder
        setTimeout(() => {
          inputRef.focus();
          const newPosition = cursorPosition + placeholder.length;
          inputRef.setSelectionRange(newPosition, newPosition);
        }, 0);
      } else {
        // Fallback if ref not available
        const currentValue = message[field] || '';
        const updatedValue = `${currentValue} ${placeholder}`.trim();
        updateMessage(messageId, field, updatedValue);
      }
    } else {
      // For textarea (SMS body), insert at cursor position
      const currentValue = message[field] || '';
      const updatedValue = `${currentValue} ${placeholder}`.trim();
      updateMessage(messageId, field, updatedValue);
    }
  };

  const requireAiContext = () => {
    if (!industry.trim() || !campaignGoal.trim()) {
      setAiError('Please provide an industry and campaign goal before using AI assistance.');
      return false;
    }
    setAiError('');
    return true;
  };

  const handleGenerateSubject = async (messageId, index) => {
    const message = messages.find(m => m.id === messageId);
    if (!message || message.type !== 'email') return;
    if (!requireAiContext()) return;

    setAiSubjectLoading(prev => ({ ...prev, [messageId]: true }));
    try {
      const previousSubjects = messages
        .filter((msg, idx) => idx < index && msg.type === 'email' && msg.subject?.trim())
        .map(msg => msg.subject.trim());

      const response = await templatesAPI.aiGenerateSubject({
        industry,
        goal: campaignGoal,
        message_context: index === 0 ? 'Initial outreach email' : `Follow-up email #${index}`,
        previous_subjects: previousSubjects.length ? previousSubjects : undefined
      });

      const subject = response.data?.subject || response.data?.subject_line;
      if (subject) {
        updateMessage(messageId, 'subject', subject);
      }
    } catch (error) {
      setAiError(error.response?.data?.error || 'Failed to generate subject. Please try again.');
    } finally {
      setAiSubjectLoading(prev => ({ ...prev, [messageId]: false }));
    }
  };

  const handleGenerateBody = async (messageId, index) => {
    const message = messages.find(m => m.id === messageId);
    if (!message) return;
    if (!requireAiContext()) return;

    setAiBodyLoading(prev => ({ ...prev, [messageId]: true }));
    try {
      const previousMessages = messages
        .filter((msg, idx) => idx < index && msg.body?.trim())
        .map(msg => ({
          type: msg.type,
          body: msg.body.trim()
        }));

      const response = await templatesAPI.aiGenerateMessage({
        message_type: message.type,
        industry,
        goal: campaignGoal,
        message_context: index === 0 ? 'Initial outreach message' : `Follow-up message #${index}`,
        previous_messages: previousMessages.length ? previousMessages : undefined
      });

      const body = response.data?.body;
      if (body) {
        updateMessage(messageId, 'body', body);
      }
    } catch (error) {
      setAiError(error.response?.data?.error || 'Failed to generate message content. Please try again.');
    } finally {
      setAiBodyLoading(prev => ({ ...prev, [messageId]: false }));
    }
  };

  const handleSave = () => {
    setValidationError('');

    if (!templateName.trim()) {
      setValidationError('Please enter a template name');
      return;
    }

    for (const msg of messages) {
      if (!msg.body.trim()) {
        setValidationError('All messages must have body content');
        return;
      }
      if (msg.type === 'email' && !msg.subject.trim()) {
        setValidationError('Email messages must have a subject');
        return;
      }
    }

    const json_config = {
      channels: [...new Set(messages.map(m => m.type))],
      messages: messages.map((msg, index) => {
        // Convert delay_value and delay_unit to delay_hours
        const delayValue = msg.delay_value || 0;
        const delayHours = msg.delay_unit === 'days' ? delayValue * 24 : delayValue;

        return {
          id: index + 1,
          type: msg.type,
          delay_hours: Number.isFinite(delayHours) ? delayHours : 0,
          ...(msg.type === 'email' ? { subject: msg.subject, provider: emailProvider } : {}),
          body: msg.body
        };
      }),
      metadata: {
        industry,
        location,
        campaign_goal: campaignGoal,
        emailProvider
      }
    };

    const templateData = {
      name: templateName,
      description,
      industry,
      location,
      json_config
    };

    if (initialData?.id) {
      templateData.id = initialData.id;
    }

    onSave(templateData);
  };

  return (
    <div className="template-builder">
      <div className="template-builder-body">
        <h3 className="tb-section-title">Details</h3>
        <div className="form-group">
          <label>Template Name *</label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., Welcome Series - Real Estate"
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            rows={2}
          />
        </div>

        <div className="metadata-grid">
          <div className="form-group">
            <label>Industry (for AI)</label>
            <input
              type="text"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              placeholder="e.g., Tree Surgeons"
            />
          </div>
          <div className="form-group">
            <label>Location</label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g., Bath"
            />
          </div>
          <div className="form-group">
            <label>Campaign Goal / Purpose</label>
            <input
              type="text"
              value={campaignGoal}
              onChange={(e) => setCampaignGoal(e.target.value)}
              placeholder="e.g., Generate consultations for spring season"
            />
          </div>
          <div className="form-group">
            <label>Email Provider</label>
            <select
              value={emailProvider}
              onChange={(e) => setEmailProvider(e.target.value)}
            >
              <option value="smtp">SMTP</option>
              {emailProvider === 'sendgrid' && <option value="sendgrid">SendGrid</option>}
              <option value="resend">Resend</option>
            </select>
            <small className="text-muted">
              All email messages in this template will use <strong>{emailProvider.toUpperCase()}</strong>
            </small>
          </div>
        </div>

        {aiError && <div className="ai-error"><SetupErrorMessage message={aiError} /></div>}

        <div className="placeholders-info">
          <h4>Available Placeholders:</h4>
          <div className="placeholders-list">
            {AVAILABLE_PLACEHOLDERS.map(p => (
              <span key={p.value} className="placeholder-badge">{p.value}</span>
            ))}
          </div>
        </div>

        <div className="messages-section">
          <h3 className="tb-section-title">Messages ({messages.length})</h3>

          {messages.map((message, index) => (
            <div key={message.id} className="message-card">
              <div className="message-header">
                <div className="tb-step">
                  <span className="tb-step-badge">{index + 1}</span>
                  <span className="message-number">Message {index + 1}</span>
                  <span className="tb-step-meta">
                    {message.type === 'sms' ? 'SMS' : 'Email'}
                    {' · '}
                    {index === 0
                      ? 'sends first'
                      : `${message.delay_value || 0} ${message.delay_unit || 'hours'} after previous`}
                  </span>
                </div>
                {messages.length > 1 && (
                  <button
                    type="button"
                    className="tb-remove-btn"
                    onClick={() => removeMessage(message.id)}
                    title="Remove message"
                    aria-label={`Remove message ${index + 1}`}
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div className="message-config">
                <div className="form-group">
                  <label>Channel</label>
                  <select
                    value={message.type}
                    onChange={(e) => updateMessage(message.id, 'type', e.target.value)}
                  >
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Delay after previous message</label>
                  <div className="tb-delay-row">
                    <input
                      type="number"
                      min="0"
                      className="tb-delay-value"
                      value={message.delay_value || 0}
                      onChange={(e) => updateMessage(
                        message.id,
                        'delay_value',
                        Number.isNaN(parseInt(e.target.value, 10)) ? 0 : parseInt(e.target.value, 10)
                      )}
                    />
                    <select
                      className="tb-delay-unit"
                      value={message.delay_unit || 'hours'}
                      onChange={(e) => updateMessage(message.id, 'delay_unit', e.target.value)}
                    >
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </select>
                  </div>
                  {index === 0 && <small className="text-muted">First message sends immediately</small>}
                </div>

                {message.type === 'email' && (
                  <div className="form-group full-width">
                    <label>Subject *</label>
                    <div className="input-with-placeholders">
                      <input
                        type="text"
                        ref={(el) => subjectInputRefs.current[message.id] = el}
                        value={message.subject}
                        onChange={(e) => updateMessage(message.id, 'subject', e.target.value)}
                        placeholder="Email subject"
                      />
                      <div className="placeholder-buttons">
                        {AVAILABLE_PLACEHOLDERS.map(p => (
                          <button
                            key={p.value}
                            type="button"
                            onClick={() => insertPlaceholder(message.id, 'subject', p.value)}
                            className="btn-placeholder"
                            title={`Insert ${p.label}`}
                          >
                            {p.value}
                          </button>
                        ))}
                      </div>
                      <div className="ai-actions">
                        <button
                          type="button"
                          className="btn-ai"
                          disabled={!!aiSubjectLoading[message.id]}
                          onClick={() => handleGenerateSubject(message.id, index)}
                        >
                          <Sparkles size={14} />
                          {aiSubjectLoading[message.id] ? 'Generating...' : 'AI Generate Subject'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="form-group full-width">
                  <label>Message Body *</label>
                  {message.type === 'email' && (
                    <div className="tb-editor-switch">
                      <span className="tb-editor-switch-label">
                        {htmlEditorStates[message.id] ? 'HTML Editor' : 'Visual Editor'}
                      </span>
                      <button
                        type="button"
                        className="tb-mini-btn"
                        onClick={() => setHtmlEditorStates(prev => ({ ...prev, [message.id]: !prev[message.id] }))}
                      >
                        {htmlEditorStates[message.id] ? '← Visual Editor' : 'HTML Editor →'}
                      </button>
                    </div>
                  )}
                  <div className="input-with-placeholders">
                    {message.type === 'email' ? (
                      htmlEditorStates[message.id] ? (
                        <textarea
                          value={message.body}
                          onChange={(e) => updateMessage(message.id, 'body', e.target.value)}
                          placeholder="Paste HTML here..."
                          rows={10}
                          className="tb-code"
                        />
                      ) : (
                        <ReactQuill
                          ref={(el) => quillRefs.current[message.id] = el}
                          value={message.body}
                          onChange={(content) => updateMessage(message.id, 'body', content)}
                          modules={quillModules}
                          placeholder="Message content..."
                          theme="snow"
                        />
                      )
                    ) : (
                      <textarea
                        value={message.body}
                        onChange={(e) => updateMessage(message.id, 'body', e.target.value)}
                        placeholder="Message content..."
                        rows={6}
                      />
                    )}
                    <div className="placeholder-buttons">
                      {AVAILABLE_PLACEHOLDERS.map(p => (
                        <button
                          key={p.value}
                          type="button"
                          onClick={() => insertPlaceholder(message.id, 'body', p.value)}
                          className="btn-placeholder"
                          title={`Insert ${p.label}`}
                        >
                          {p.value}
                        </button>
                      ))}
                    </div>
                    <div className="ai-actions">
                      <button
                        type="button"
                        className="btn-ai"
                        disabled={!!aiBodyLoading[message.id]}
                        onClick={() => handleGenerateBody(message.id, index)}
                      >
                        <Sparkles size={14} />
                        {aiBodyLoading[message.id] ? 'Generating...' : 'AI Generate Message'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <button type="button" className="btn btn-secondary tb-add-btn" onClick={addMessage}>
            <Plus size={16} /> Add Another Message
          </button>
        </div>
      </div>

      <div className="template-builder-footer">
        {validationError ? (
          <span className="tb-footer-hint tb-validation" role="alert">
            {validationError}
          </span>
        ) : (
          <span className="tb-footer-hint">
            Messages send in order; each delay counts from the previous message.
          </span>
        )}
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave}>
          Save Template
        </button>
      </div>
    </div>
  );
};

export default TemplateBuilder;
